<script setup lang="ts">
const {
  activities,
  heading = 'Latest recordings',
  showAllTo = '/sails/',
  showAllLabel = 'Show all',
  showUser = true,
  showSignupPrompt = false,
} = defineProps<{
  activities: Array<Activity>,
  heading?: string,
  showAllTo?: string,
  showAllLabel?: string,
  showUser?: boolean,
  showSignupPrompt?: boolean,
}>()

const store = useMainStore()
const colors = store.colors
const { formatSpeed } = useSpeedUnit()

// Each tile's map pulls in the OpenLayers bundle; mounting all of them up front
// (e.g. on the homepage) forces that ~300KB chunk to execute during initial load
// even though most tiles are below the fold. Defer each map until its card
// actually scrolls into view.
const visibleMapIds = ref<Set<number>>(new Set())
const onTileIntersect = (activityId: number, isIntersecting: boolean) => {
  if (isIntersecting) visibleMapIds.value = new Set(visibleMapIds.value).add(activityId)
}
</script>

<template>
  <v-row v-if="activities.length" class="mt-6">
    <v-col cols="12" class="d-flex align-center justify-space-between">
      <h2 class="text-h4">{{ heading }}</h2>
      <v-btn variant="tonal" color="primary" :to="showAllTo" nuxt>{{ showAllLabel }}</v-btn>
    </v-col>
    <v-col
      v-for="activity in activities"
      :key="activity.id"
      cols="12"
      sm="6"
      xl="3"
      class="d-flex"
    >
      <div class="fill-height w-100" style="cursor:pointer" @click="navigateTo(`/sails/${activity.id}`)">
        <v-card class="fill-height">
          <v-card-text class="pa-3">
            <div
              v-if="showUser || activity.visible === false"
              class="d-flex align-center justify-space-between ga-2"
            >
              <UserInTable v-if="showUser" :user="activity.user_details" />
              <v-chip
                v-if="activity.visible === false"
                size="x-small"
                color="warning"
                variant="tonal"
                prepend-icon="mdi-eye-off"
                class="ml-auto"
              >
                Hidden
              </v-chip>
            </div>
            <v-row no-gutters :class="showUser ? 'mt-2' : ''">
              <v-col cols="6">
                <v-list density="compact" slim>
                  <v-list-item v-if="activity.scoring_speed_overall_ms" class="pl-0" prepend-icon="mdi-speedometer">
                    <v-list-item-subtitle>{{ formatSpeed(activity.scoring_speed_overall_ms) }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item class="pl-0" prepend-icon="mdi-calendar">
                    <v-list-item-subtitle>{{ niceDate(activity.start_time) }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-col>
              <v-col cols="6">
                <v-list density="compact" slim>
                  <v-list-item v-if="activity.boat_details" class="pl-0" prepend-icon="mdi-sail-boat">
                    <v-list-item-subtitle>{{ activity.boat_details?.name }}</v-list-item-subtitle>
                  </v-list-item>
                  <v-list-item v-if="activity.venue_details" class="pl-0" prepend-icon="mdi-map-marker">
                    <v-list-item-subtitle>{{ activity.venue_details?.name }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-col>
            </v-row>
          </v-card-text>
          <div v-intersect.once="(isIntersecting: boolean) => onTileIntersect(activity.id, isIntersecting)">
            <Map
              v-if="visibleMapIds.has(activity.id)"
              :activities="[activity]"
              :colors="[colors[0]]"
              :min-height="130"
              :show-view-controls="false"
              :show-zoom-buttons="false"
              geo-json-key="simplified_geojson"
              :color-source="'default'"
              :vmc-reference-heading="null"
              :course="activity.race_details?.course_details || activity.course_details"
            />
            <v-skeleton-loader v-else type="image" :height="130" />
          </div>
        </v-card>
      </div>
    </v-col>

    <v-col v-if="showSignupPrompt" cols="12" sm="6" xl="3" class="d-flex">
      <v-card class="fill-height w-100 d-flex flex-column">
        <v-card-text class="pa-3">
          <v-row no-gutters>
            <v-col cols="6">
              <v-list density="compact" slim>
                <v-list-item class="pl-0" prepend-icon="mdi-speedometer">
                  <v-list-item-subtitle class="text-disabled">— kts</v-list-item-subtitle>
                </v-list-item>
                <v-list-item class="pl-0" prepend-icon="mdi-calendar">
                  <v-list-item-subtitle class="text-disabled">Today</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="6">
              <v-list density="compact" slim>
                <v-list-item class="pl-0" prepend-icon="mdi-sail-boat">
                  <v-list-item-subtitle class="text-disabled">Your boat</v-list-item-subtitle>
                </v-list-item>
                <v-list-item class="pl-0" prepend-icon="mdi-map-marker">
                  <v-list-item-subtitle class="text-disabled">Your venue</v-list-item-subtitle>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>
        </v-card-text>
        <v-sheet class="d-flex flex-column align-center justify-center flex-grow-1 pa-4 text-center" color="primary" rounded="0">
          <v-icon size="32" color="white" class="mb-2">mdi-map-marker-path</v-icon>
          <div class="text-body-2 text-white font-weight-bold mb-1">Your recording here?</div>
          <div class="text-caption text-white mb-3">Sign up and start tracking your sailing sessions</div>
          <v-btn to="/account/signup" color="white" variant="elevated" size="small" rounded>Sign up now</v-btn>
        </v-sheet>
      </v-card>
    </v-col>
  </v-row>
</template>
